import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface Testimonial {
  id: string;
  name: string;
  country: string;
  date: string;
  rating: number;
  comment: string;
  visible: boolean;
  created_at: string;
  updated_at: string;
}

export const useTestimonials = (adminMode = false) => {
  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const fetchTestimonials = async () => {
    try {
      setLoading(true);
      let query = supabase
        .from('testimonials')
        .select('*')
        .order('created_at', { ascending: false });

      // Only fetch visible testimonials for public view
      if (!adminMode) {
        query = query.eq('visible', true);
      }

      const { data, error } = await query;

      if (error) throw error;
      setTestimonials(data || []);
    } catch (error) {
      console.error('Error fetching testimonials:', error);
      toast({
        title: "Erreur",
        description: "Impossible de charger les avis",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const addTestimonial = async (testimonialData: Omit<Testimonial, 'id' | 'created_at' | 'updated_at'>) => {
    try {
      const { data, error } = await supabase
        .from('testimonials')
        .insert([testimonialData])
        .select()
        .single();

      if (error) throw error;
      
      setTestimonials(prev => [data, ...prev]);
      toast({
        title: "Succès",
        description: "Avis ajouté avec succès",
      });
      return { success: true };
    } catch (error) {
      console.error('Error adding testimonial:', error);
      toast({
        title: "Erreur",
        description: "Impossible d'ajouter l'avis",
        variant: "destructive",
      });
      return { success: false };
    }
  };

  const updateTestimonial = async (id: string, updates: Partial<Testimonial>) => {
    try {
      const { data, error } = await supabase
        .from('testimonials')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      
      setTestimonials(prev => 
        prev.map(testimonial => 
          testimonial.id === id ? data : testimonial
        )
      );
      toast({
        title: "Succès",
        description: "Avis mis à jour avec succès",
      });
      return { success: true };
    } catch (error) {
      console.error('Error updating testimonial:', error);
      toast({
        title: "Erreur",
        description: "Impossible de mettre à jour l'avis",
        variant: "destructive",
      });
      return { success: false };
    }
  };

  const deleteTestimonial = async (id: string) => {
    try {
      const { error } = await supabase
        .from('testimonials')
        .delete()
        .eq('id', id);

      if (error) throw error;
      
      setTestimonials(prev => prev.filter(testimonial => testimonial.id !== id));
      toast({
        title: "Succès",
        description: "Avis supprimé avec succès",
      });
      return { success: true };
    } catch (error) {
      console.error('Error deleting testimonial:', error);
      toast({
        title: "Erreur",
        description: "Impossible de supprimer l'avis",
        variant: "destructive",
      });
      return { success: false };
    }
  };

  const toggleVisibility = async (id: string, visible: boolean) => {
    return updateTestimonial(id, { visible });
  };

  useEffect(() => {
    fetchTestimonials();
  }, [adminMode]);

  return {
    testimonials,
    loading,
    addTestimonial,
    updateTestimonial,
    deleteTestimonial,
    toggleVisibility,
    refetch: fetchTestimonials,
  };
};